import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { assertReportAdmission } from "../nativeConditions.mjs";
import { admitInternalSource, assertDesignerAdapterAdmission, inheritInternalSession, type InternalDesignerSession } from "./internalConditionalSession";
import {
  asArray,
  asRecord,
  cleanRecord,
  cloneValue,
  denormalizePageSize,
  DesignerBand,
  DesignerColumn,
  DesignerComponent,
  DesignerDocument,
  DesignerField,
  DesignerPageSettings,
  JsonRecord,
  normalizeOrientation,
  normalizePageSize,
  primaryComponent,
} from "./reportDesignerModel";

const TOP_LEVEL_KEYS = new Set([
  "definitionId",
  "definitionVersion",
  "schemaVersion",
  "reportKey",
  "reportType",
  "scope",
  "overrideOf",
  "name",
  "roles",
  "requiredFilters",
  "capabilities",
  "data",
  "datasetContract",
  "output",
  "metadata",
  "assets",
  "datasets",
  "layout",
]);

const LAYOUT_KEYS = new Set(["styles", "outputs", "sections"]);
const SECTION_KEYS = new Set(["id", "type", "dataset", "outputs", "style", "fields", "columns", "content"]);
const FIELD_KEYS = new Set(["id", "label", "key", "format", "horizontalAlign", "style", "column", "row", "marginTop"]);
const COLUMN_KEYS = new Set(["id", "label", "key", "format", "horizontalAlign", "width"]);

export function legacyPayloadToDesignerDocument(payload: FormattedInvoicePayload, internalSession?: InternalDesignerSession): DesignerDocument {
  assertDesignerAdapterAdmission(payload, "import", internalSession);
  const source = cloneValue(payload) as FormattedInvoicePayload & JsonRecord;
  const layout = asRecord(source.layout);
  const outputs = asRecord(layout.outputs);
  const pdfOutput = asRecord(outputs.pdf);
  const nativePage = source.schemaVersion === "reports-v3.1" ? asRecord(layout.page) : null;
  const page: DesignerPageSettings = {
    size: normalizePageSize(nativePage ? nativePage.size : pdfOutput.pageSize),
    orientation: normalizeOrientation(nativePage ? nativePage.orientation : pdfOutput.orientation),
    margins: asRecord(nativePage ? nativePage.margins : pdfOutput.margins),
  };

  const sections = asArray(layout.sections).map(asRecord);
  const document: DesignerDocument = {
    schemaVersion: "reports-v3.1",
    sourceSchemaVersion: typeof source.schemaVersion === "string" ? source.schemaVersion : undefined,
    definitionId: source.definitionId,
    definitionVersion: source.definitionVersion,
    reportKey: stringOrUndefined(source.reportKey),
    reportType: stringOrUndefined(source.reportType),
    scope: stringOrUndefined(source.scope),
    overrideOf: stringOrUndefined(source.overrideOf),
    name: stringOrUndefined(source.name),
    roles: Array.isArray(source.roles) ? cloneValue(source.roles) : undefined,
    requiredFilters: optionalRecord(source.requiredFilters),
    capabilities: optionalRecord(source.capabilities),
    data: source.data,
    datasetContract: optionalRecord(source.datasetContract),
    output: source.output,
    metadata: asRecord(source.metadata),
    assets: asRecord(source.assets),
    datasets: asRecord(source.datasets),
    page,
    styles: asRecord(layout.styles),
    outputs,
    bands: sections.map(sectionToBand),
    renderer: {
      topLevel: pickUnmapped(source, TOP_LEVEL_KEYS),
      layout: source.schemaVersion === "reports-v3.1" ? cloneValue(layout) : pickUnmapped(layout, LAYOUT_KEYS),
    },
  };
  if (internalSession) admitInternalSource(document, internalSession);
  return document;
}

export function designerDocumentToLegacyPayload(document: DesignerDocument, internalSession?: InternalDesignerSession): FormattedInvoicePayload {
  assertDesignerAdapterAdmission(document, "serialize", internalSession);
  const native = document.sourceSchemaVersion === "reports-v3.1";
  const layoutOutputs = cloneValue(document.outputs || {});
  if (!native && layoutOutputs.pdf !== undefined) {
    const pdfOutput = { ...asRecord(layoutOutputs.pdf) };
    pdfOutput.pageSize = denormalizePageSize(document.page.size);
    pdfOutput.orientation = document.page.orientation;
    if (document.page.margins && Object.keys(document.page.margins).length) {
      pdfOutput.margins = document.page.margins;
    }
    layoutOutputs.pdf = pdfOutput;
  }

  const payload: JsonRecord = cleanRecord({
    ...cloneValue(document.renderer.topLevel),
    definitionId: document.definitionId,
    definitionVersion: document.definitionVersion,
    schemaVersion: document.sourceSchemaVersion,
    reportKey: document.reportKey,
    reportType: document.reportType,
    scope: document.scope,
    overrideOf: document.overrideOf,
    name: document.name,
    roles: document.roles,
    requiredFilters: document.requiredFilters,
    capabilities: document.capabilities,
    data: document.data,
    datasetContract: document.datasetContract,
    output: document.output,
    metadata: document.metadata,
    assets: document.assets,
    datasets: document.datasets,
    layout: {
      ...cloneValue(document.renderer.layout),
      ...(!native || "styles" in document.renderer.layout || Object.keys(document.styles).length ? { styles: document.styles } : {}),
      ...(Object.keys(layoutOutputs).length || (native && "outputs" in document.renderer.layout) ? { outputs: layoutOutputs } : {}),
      ...(!native ? { sections: document.bands.map(bandToSection) } : {}),
    },
  });

  if (internalSession) inheritInternalSession(document, payload);
  return payload as FormattedInvoicePayload;
}

export function parseDesignerDocumentJson(text: string, internalSession?: InternalDesignerSession): { document: DesignerDocument | null; error: string } {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (internalSession) admitInternalSource(asRecord(parsed), internalSession);
    else assertReportAdmission(parsed, "import");
    const record = asRecord(parsed);
    if (record.schemaVersion === "reports-v3.1" && Array.isArray(record.bands) && record.renderer) {
      return { document: parsed as DesignerDocument, error: "" };
    }
    return { document: legacyPayloadToDesignerDocument(parsed as FormattedInvoicePayload, internalSession), error: "" };
  } catch (error) {
    return { document: null, error: error instanceof Error ? error.message : "Invalid JSON." };
  }
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function sectionToBand(section: JsonRecord, index: number): DesignerBand {
  const legacyType = String(section.type || "section");
  const bandId = makeStableId(section.id, `band-${index + 1}`);
  const component = sectionToComponent(section, legacyType, bandId, index);
  return {
    id: bandId,
    legacyId: typeof section.id === "string" ? section.id : undefined,
    role: bandRole(legacyType),
    title: String(section.title || bandLabel(legacyType)),
    componentIds: [component.id],
    components: [component],
    renderer: pickUnmapped(section, SECTION_KEYS),
  };
}

function sectionToComponent(section: JsonRecord, legacyType: string, bandId: string, sectionIndex: number): DesignerComponent {
  const base = {
    id: `${bandId}__${legacyType || "section"}`,
    datasetId: stringOrUndefined(section.dataset),
    styleId: stringOrUndefined(section.style),
    outputs: asArray(section.outputs).map(String),
    renderer: pickUnmapped(section, SECTION_KEYS),
  };

  if (legacyType === "keyValueGrid") {
    return {
      ...base,
      kind: "keyValueGrid",
      fields: asArray(section.fields).map((field, fieldIndex) => fieldToDesignerField(asRecord(field), base.id, fieldIndex)),
    };
  }

  if (legacyType === "table") {
    return {
      ...base,
      kind: "table",
      columns: asArray(section.columns).map((column, columnIndex) => columnToDesignerColumn(asRecord(column), base.id, columnIndex)),
    };
  }

  if (legacyType === "text") {
    return {
      ...base,
      kind: "text",
      content: typeof section.content === "string" ? section.content : undefined,
    };
  }

  return {
    ...base,
    id: `${bandId}__renderer-${sectionIndex + 1}`,
    kind: "rendererSection",
    legacyType,
  };
}

function fieldToDesignerField(field: JsonRecord, componentId: string, index: number): DesignerField {
  const id = makeStableId(field.id, `${componentId}__field-${index + 1}`);
  return {
    id,
    legacyId: id,
    label: stringOrUndefined(field.label),
    key: stringOrUndefined(field.key),
    format: stringOrUndefined(field.format),
    horizontalAlign: stringOrUndefined(field.horizontalAlign),
    styleId: stringOrUndefined(field.style),
    column: numberOrUndefined(field.column),
    row: numberOrUndefined(field.row),
    marginTop: numberOrUndefined(field.marginTop),
    renderer: pickUnmapped(field, FIELD_KEYS),
  };
}

function columnToDesignerColumn(column: JsonRecord, componentId: string, index: number): DesignerColumn {
  const id = makeStableId(column.id, `${componentId}__column-${index + 1}`);
  return {
    id,
    legacyId: id,
    label: stringOrUndefined(column.label),
    key: stringOrUndefined(column.key),
    format: stringOrUndefined(column.format),
    horizontalAlign: stringOrUndefined(column.horizontalAlign),
    width: numberOrUndefined(column.width),
    renderer: pickUnmapped(column, COLUMN_KEYS),
  };
}

function bandToSection(band: DesignerBand): JsonRecord {
  const component = primaryComponent(band);
  if (!component) return { id: band.legacyId || band.id, type: "section", ...cloneValue(band.renderer) };

  const sectionBase = cleanRecord({
    ...cloneValue(band.renderer),
    ...cloneValue(component.renderer),
    id: band.legacyId || band.id,
    type: component.kind === "rendererSection" ? component.legacyType : component.kind,
    dataset: "datasetId" in component ? component.datasetId : undefined,
    outputs: component.outputs?.length ? component.outputs : undefined,
    style: "styleId" in component ? component.styleId : undefined,
  });

  if (component.kind === "keyValueGrid") {
    return {
      ...sectionBase,
      fields: component.fields.map(designerFieldToField),
    };
  }
  if (component.kind === "table") {
    return {
      ...sectionBase,
      columns: component.columns.map(designerColumnToColumn),
    };
  }
  if (component.kind === "text") {
    return cleanRecord({
      ...sectionBase,
      content: component.content,
    });
  }
  return sectionBase;
}

function designerFieldToField(field: DesignerField): JsonRecord {
  return cleanRecord({
    ...cloneValue(field.renderer),
    id: field.legacyId,
    label: field.label,
    key: field.key,
    format: field.format,
    horizontalAlign: field.horizontalAlign,
    style: field.styleId,
    column: field.column,
    row: field.row,
    marginTop: field.marginTop,
  });
}

function designerColumnToColumn(column: DesignerColumn): JsonRecord {
  return cleanRecord({
    ...cloneValue(column.renderer),
    id: column.legacyId,
    label: column.label,
    key: column.key,
    format: column.format,
    horizontalAlign: column.horizontalAlign,
    width: column.width,
  });
}

function pickUnmapped(record: JsonRecord, mappedKeys: Set<string>): JsonRecord {
  return Object.fromEntries(Object.entries(record).filter(([key]) => !mappedKeys.has(key)));
}

function optionalRecord(value: unknown): JsonRecord | undefined {
  const record = asRecord(value);
  return Object.keys(record).length ? record : undefined;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function numberOrUndefined(value: unknown): number | undefined {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function makeStableId(value: unknown, fallback: string): string {
  const text = typeof value === "string" ? value.trim() : "";
  return text || fallback;
}

function bandRole(type: string): DesignerBand["role"] {
  if (type === "companyHeader" || type === "keyValueGrid") return "reportHeader";
  if (type === "repeat" || type === "table") return "details";
  if (type === "signatureBlock") return "reportFooter";
  return "custom";
}

function bandLabel(type: string): string {
  if (type === "companyHeader") return "Report Header";
  if (type === "keyValueGrid") return "Report Header";
  if (type === "table") return "Details";
  if (type === "repeat") return "Repeating Details";
  if (type === "signatureBlock") return "Report Footer";
  return titleize(type || "Band");
}

function titleize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
